<template>
  <div class="source-content graphics">
    <div class="p-[0.625rem]">
      <search-filter
        :value="search"
        :options="sortedGraphicsTags"
        :selected="selectedTags"
        data-test="producer-assets-displayElements-searchFilter"
        @update:value="changeGraphicsSearch"
        @update:selected="onUpdateSelectedTags"
      />
    </div>
    <!-- No graphic templates after searching -->
    <div v-if="!isLoading && !total" class="py-8 text-center">
      <h3 class="mb-4 text-[14px] font-semibold">
        No graphics found
      </h3>

      <button
        class="tb-btn tb-btn-contained medium-em px-3"
        data-entity="producer-assets-resetTemplateSearch"
        data-test="producer-assets-displayElements-resetSearch"
        @click="clearTags"
      >
        Reset Search
      </button>
    </div>

    <div
      ref="sourceList"
      class="source-list"
      data-test="producer-assets-displayElements-list"
    >
      <draggable
        :list="templates"
        :clone="clone"
        :sort="false"
        animation="200"
        :group="{ name: 'timeline', pull: 'clone', put: false }"
        @start="startDrag"
        @end="$store.dispatch('clearDragType')"
        revert-on-spill="true"
      >
        <div
          v-for="template in templates"
          :key="template._id"
          class="source-item graphic"
          :data-template-id="template._id"
          data-entity="producer-assets-template"
          :data-test="`producer-assets-element:${template._id}`"
        >
          <div class="tb-checkbox">
            <input
              type="checkbox"
              :id="template._id"
              :value="template"
              v-model="selected"
              data-entity="producer-assets-toggleTemplateSelection"
              :data-test="`producer-assets-element-checkBox:${template._id}`"
            >

            <label :for="template._id" />
          </div>

          <div class="source-item-wrapper">
            <img class="preview mr-2" :src="template.preview_url" :data-test="`producer-assets-element-preview:${template._id}`" />

            <div class="source-item-info-wrapper">
              <div class="source-title title-text" :title="template.title" :data-test="`producer-assets-element-title:${template._id}`">
                {{ template.title }}
              </div>

                <div
                  :class="{
                    'source-item-tags': true,
                    'mt-1': template.tags?.length,
                  }"
                  :data-test="`producer-assets-element-tags:${template._id}`"
                >
                  <span
                    v-for="tag in displayTags(template.tags)"
                    :key="tag"
                    class="tag"
                    :data-test="`producer-assets-element-tags:${template._id}_${tag}`"
                  >
                    {{ tag }}
                  </span>

                  <tag-overflow-popover
                    v-if="tagOverflowCount(template.tags)"
                    :tags="template.tags"
                    :data-test="`producer-assets-element-tags:${template._id}_overflow`"
                  />
                </div>
            </div>

            <div class="source-actions ml-1">
              <button
                class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                title="Add to playlist"
                data-entity="producer-assets-addTemplateToPlaylist"
                :data-test="`producer-assets-element-addToPlaylist:${template._id}`"
                @click="addToPlaylist(template, 'Graphic')"
              >
                <span class="fas fa-plus" />
              </button>
            </div>
          </div>
        </div>
      </draggable>

      <div ref="loadTrigger" class="load-trigger flex min-h-16 w-full items-center justify-center">
        <div v-if="isLoading" class="loading loading--inline m-0" data-test="producer-assets-displayElements-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    </div>

    <div
      v-if="selected.length"
      class="producer-assets-selected flex items-center justify-end p-4"
      data-test="producer-assets-selected"
    >
      <p
        class="producer-assets-selected__count mb-0 mr-4 mt-0 text-primary"
        data-test="producer-assets-selected-count"
      >
        {{ selected.length }} selected
      </p>

      <tgb-split-button
        :options="splitButtonOptions"
        :disabled="!selected.length"
        split-button-label="Add group"
        data-entity="producer-assets-addSelectedTemplatesToPlaylist"
        data-test="producer-assets-addElementsToPlaylist"
        @click="addSelectedToPlaylist('Graphics')"
      >
        Add to playlist
      </tgb-split-button>
    </div>
  </div>
</template>

<script>
import * as uuid from 'uuid';
import { mapState, mapActions } from 'vuex';
import { pick } from 'lodash-es';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import TagOverflowPopover from '@/js/shared/TagOverflowPopover.vue';
import tabMixins from '../../mixins/tab-mixins';
import SplitButton from '../../components/split-button/SplitButton.vue';
import ConduitEventsService from '../../services/conduitEventsService';

import toast from '../../components/toast/toast';

export default {
  mixins: [tabMixins],

  components: {
    'tgb-split-button': SplitButton,
    SearchFilter,
    TagOverflowPopover,
  },

  data() {
    return {
      showTemplatePicker: false,
      selectedGraphic: null,
    };
  },

  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  watch: {
    templates() {
      if (!this.$refs.sourceList || !this.templates.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },

  created() {
    this.$store.dispatch('getTemplates').catch((err) => {
      console.error('Error loading templates:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load templates.',
      });
    });
  },

  beforeUnmount() {
    this.$store.dispatch('setGraphics', []);
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  },

  computed: {
    ...mapState({
      isLoading: (state) => state.graphics.isLoading,
      search: (state) => state.graphics.search,
      templates: (state) => state.graphics.templates,
      lookupTable: (state) => state.graphics.templatesLookupTable,
      total: (state) => state.graphics.total,
      selectedTags: (state) => state.graphics.tags,
      sortedGraphicsTags: (state) => state.graphics.templateTags,
    }),

    splitButtonOptions() {
      const groups = this.$store.state.production.production.timeline.filter((item) => (
        item.layout === 'graphics'
      ));

      const options = groups.map((group) => ({
        key: group.timeline_id,
        label: group.title,
        onClick: () => {
          this.addSelectedToGroup('Graphics', group.timeline_id);
        },
      }));

      if (options.length) {
        options.push({ divider: true });
      }

      // Option to create a new group and add to that instead
      options.push({
        key: 'new',
        label: '+ New Group',
        onClick: async () => {
          const posts = this.selected.map(this.clone);

          await this.$store.dispatch('addGraphicsGroup', { posts });

          this.selected = [];

          await this.$store.dispatch('getTimelineTemplates');
          await this.$store.dispatch('generateShortUrls', posts.map((item) => item.timeline_id));
          this.$store.dispatch('trackSportradarInGameElement', posts.map((item) => item.timeline_id));
          this.$store.dispatch('updateTimeline');

          ConduitEventsService.track(
            'producer.created.group-from-menu',
            {
              production_id: this.production._id,
            },
            {
              ownerId: this.production.owner,
              userId: this.$store.state.user.id,
              isStaff: this.$store.getters.isStaff,
            },
          );

          ConduitEventsService.track(
            'producer.added.items-to-playlist',
            {
              type: 'Graphic',
              count: posts?.length || 1,
              production_id: this.production._id,
              component: 'GraphicTemplates.vue',
              method: 'addSelectedToGroup',
              items: posts,
            },
            {
              ownerId: this.production.owner,
              userId: this.$store.state.user.id,
              isStaff: this.$store.getters.isStaff,
            },
          );
        },
      });

      return options;
    },
  },

  methods: {
    ...mapActions([
      'changeGraphicsSearch',
    ]),

    onUpdateSelectedTags(tags) {
        this.$store.dispatch('changeGraphicTags', tags);
    },

    displayTags(tags = []) {
      return tags.slice(0, 2);
    },

    tagOverflowCount(tags = []) {
      return Math.max((tags?.length || 0) - 2, 0);
    },

    /**
     * Force fetch graphics again, jic more graphics were added since user entered producer.
     */
    reloadGraphics() {
      this.$store.dispatch('resetTemplates');
      this.$store.dispatch('getTemplates');
    },

    /**
    * Attempt to load more graphic templates
    */
    loadMore() {
      if (!this.isLoading && this.$store.getters.hasMoreTemplates) {
        this.$store.dispatch('incrementTemplatesPage');
      }
    },

    /**
    * Remove all current tags, reset page to 1, and fetch templates again.
    */
    clearTags() {
      this.$store.dispatch('resetSearch');
    },

    startDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === parseInt(templateId, 10));
      this.$store.dispatch('setDragType', {
        type: 'graphic',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },

    clone(original) {
      const clone = pick(original, ['title', 'owner', 'meta']);

      clone.template_id = original._id;

      clone.timeline_id = uuid.v1();
      clone.displayTime = 5000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.layout = 'graphic';
      clone.posts = [{ timeline_id: clone.timeline_id }];
      clone.fields = {};

      if (original.meta) {
        clone.meta = original.meta;
      }

      return clone;
    },
  },
};
</script>
